import type { ReportFilters, ReportKind, ReportTimeBasis } from './types';

type ReportFilterKey = keyof ReportFilters;

export interface ReportDefinition {
  readonly path: string;
  readonly filterKeys: readonly ReportFilterKey[];
  readonly allowedTimeBases: readonly ReportTimeBasis[];
  readonly defaultTimeBasis: ReportTimeBasis;
}

const common = ['from', 'to', 'timezone', 'timeBasis', 'partnerId', 'agentId'] as const;
const game = ['gameTypeId', 'gameId'] as const;

const definitions = Object.freeze({
  transactions: {
    path: '/api/v1/reports/transactions',
    filterKeys: [
      ...common,
      'memberId',
      'uplineMemberId',
      ...game,
      'currency',
      'status',
      'outcome',
      'username',
      'partnerUserId',
      'sort',
    ],
    allowedTimeBases: ['PLAY_AT', 'RECEIVED_AT'],
    defaultTimeBasis: 'PLAY_AT',
  },
  'agent-commissions': {
    path: '/api/v1/reports/agent-commissions',
    filterKeys: [...common, ...game, 'currency', 'entryType', 'sort'],
    allowedTimeBases: ['EFFECTIVE_AT'],
    defaultTimeBasis: 'EFFECTIVE_AT',
  },
  'affiliate-commissions': {
    path: '/api/v1/reports/affiliate-commissions',
    filterKeys: [...common, 'memberId', ...game, 'currency', 'entryType', 'username', 'sort'],
    allowedTimeBases: ['EFFECTIVE_AT'],
    defaultTimeBasis: 'EFFECTIVE_AT',
  },
  games: {
    path: '/api/v1/reports/games',
    filterKeys: [...common, ...game, 'currency', 'status', 'code', 'sort'],
    allowedTimeBases: ['PLAY_AT', 'RECEIVED_AT'],
    defaultTimeBasis: 'PLAY_AT',
  },
  registrations: {
    path: '/api/v1/reports/registrations',
    filterKeys: [
      ...common,
      'memberId',
      'uplineMemberId',
      'status',
      'code',
      'username',
      'partnerUserId',
      'sort',
    ],
    allowedTimeBases: ['REGISTERED_AT'],
    defaultTimeBasis: 'REGISTERED_AT',
  },
  'partner-reconciliation': {
    path: '/api/v1/reports/partner-reconciliation',
    filterKeys: [...common, 'currency', 'status', 'sort'],
    allowedTimeBases: ['RECEIVED_AT'],
    defaultTimeBasis: 'RECEIVED_AT',
  },
  'commission-rule-usage': {
    path: '/api/v1/reports/commission-rule-usage',
    filterKeys: [...common, ...game, 'currency', 'sort'],
    allowedTimeBases: ['PLAY_AT', 'RECEIVED_AT'],
    defaultTimeBasis: 'PLAY_AT',
  },
  'audit-activity': {
    path: '/api/v1/reports/audit-activity',
    filterKeys: [...common, 'currency', 'status', 'code', 'sort'],
    allowedTimeBases: ['OCCURRED_AT'],
    defaultTimeBasis: 'OCCURRED_AT',
  },
  wallets: {
    path: '/api/v1/reports/wallets',
    filterKeys: [...common, 'memberId', 'currency', 'status', 'username', 'partnerUserId', 'sort'],
    allowedTimeBases: ['CREATED_AT'],
    defaultTimeBasis: 'CREATED_AT',
  },
  'wallet-ledger': {
    path: '/api/v1/reports/wallet-ledger',
    filterKeys: [...common, 'memberId', 'currency', 'entryType', 'sort'],
    allowedTimeBases: ['EFFECTIVE_AT'],
    defaultTimeBasis: 'EFFECTIVE_AT',
  },
  withdrawals: {
    path: '/api/v1/reports/withdrawals',
    filterKeys: [...common, 'memberId', 'currency', 'status', 'username', 'partnerUserId', 'sort'],
    allowedTimeBases: ['CREATED_AT'],
    defaultTimeBasis: 'CREATED_AT',
  },
  'partner-credit-reconciliation': {
    path: '/api/v1/reports/partner-credit-reconciliation',
    filterKeys: [...common, 'memberId', 'currency', 'status', 'sort'],
    allowedTimeBases: ['CREATED_AT'],
    defaultTimeBasis: 'CREATED_AT',
  },
  'user-affiliate-by-game': {
    path: '/api/v1/reports/user-affiliate-by-game',
    filterKeys: [
      ...common,
      'memberId',
      'uplineMemberId',
      ...game,
      'currency',
      'username',
      'partnerUserId',
      'sort',
    ],
    allowedTimeBases: ['PLAY_AT', 'RECEIVED_AT'],
    defaultTimeBasis: 'PLAY_AT',
  },
} as const satisfies Readonly<Record<ReportKind, ReportDefinition>>);

export function reportDefinition(kind: ReportKind): ReportDefinition {
  const definition = definitions[kind];
  if (definition === undefined) throw new Error('Unsupported report kind');
  return definition;
}

export function compatibleReportTimeBasis(kind: ReportKind, value: unknown): ReportTimeBasis {
  const definition = reportDefinition(kind);
  const candidate = typeof value === 'string' ? value.trim() : '';
  return definition.allowedTimeBases.some((timeBasis) => timeBasis === candidate)
    ? (candidate as ReportTimeBasis)
    : definition.defaultTimeBasis;
}

export function normalizeFiltersForKind(
  kind: ReportKind,
  filters: object,
): Readonly<Record<string, string | number>> {
  const definition = reportDefinition(kind);
  const source = filters as Readonly<Record<string, unknown>>;
  const normalized: Record<string, string | number> = {};

  for (const key of definition.filterKeys) {
    if (key === 'timeBasis') {
      normalized.timeBasis = compatibleReportTimeBasis(kind, source.timeBasis);
      continue;
    }
    const value = source[key];
    if (typeof value === 'number') {
      normalized[key] = value;
      continue;
    }
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    normalized[key] = key === 'currency' ? trimmed.toUpperCase() : trimmed;
  }

  return normalized;
}
